import {Box} from '@mui/material';
import React, {FC, useCallback, useEffect, useRef, useState} from 'react';
import './DocViewDocListDataGrid.scss';
import {IDocViewDocListRowData} from './interfaces';
import ActionCell from './action-cell-component/ActionCell';
import FooterPanel from './footer-panel/FooterPanel';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {
  clearCheckedDocuments,
  removeCodeSameAsDocuments,
  setActiveDocument,
  setCheckedDocuments,
  setCodeSameAsDocuments,
  setDocumentCodeSameAsToastState,
  setMiniDocListRowLookup,
  setNextDocument,
  setTotalDocumentsCount,
  setWarningToast,
  storeDocIdInHistory,
  triggerCodeSameAsRendering,
  triggerRemoveCodeSameAsRendering,
} from '../docViewerUtils';

import {ToastType} from '../../../shared/constants';
import _ from 'lodash';
import {t} from 'i18next';

import {
  DocCompletedCheckResponse,
  MiniDocListRequest,
  MiniDocListResponse,
} from '../../../shared/interfaces';
import {
  getDocIsCompleted,
  getMiniDocListGridData,
  getRowNumberForDocument,
} from '../../../api/endpoints/documentApi';
import {LazyDataGrid} from '../../common/lazy-load-data-grid/LazyDataGrid';
import {ColumnDef} from '../../common/lazy-load-data-grid/types/ColumnDef';
import {LazyDataGridApi} from '../../common/lazy-load-data-grid/types/LazyDataGridApi';
import {DocViewResponse} from '../../../api/types/document.type';
import ArrModal from '../../common/arr-modal/ArrModal';
import TruncatedTooltip from './truncated-tooltip/TruncatedTooltip';

type DocViewDocListDataGridProps = {
  miniDocListheight: number;
  showFooter?: boolean;
  refreshKey?: number | string;
  codingFormUpdated?: boolean;
  panelId: string;
};

export type MiniDocRowSelection = {
  documentId: string;
  rowNumber: number;
  isCompleted?: boolean;
  docId: string;
  fileName: string;
  fileTypeCode?: string;
  miniMediaLength?: string;
  miniAudioTrimmedLength?: string;
  miniAudioDocDateTimeOnly?: string;
  docExt?: string | null;
  documentGUID?: string;
  ingestionGUID?: string;
  __InternalExcelProtectedSheets?: boolean | string | number | null | undefined;
  __InternalExcelProtectedWorkbook?:
    | boolean
    | string
    | number
    | null
    | undefined;
  __InternalHiddenProperties?: number | string | boolean | null | undefined;
  __InternalHiddenPropertiesText?: number | string | boolean | null | undefined;
  internalVideoPlayerReady?: string;
  ActualParameters?: string;
  ConceptParameters?: string;
  MinConceptScore?: string;
};
export type MiniDocListColumnInfo = {
  data: string;
  title: string;
  visible: boolean;
};

enum SortType {
  manual = 1,
  optimized = 2,
}

const ROW_HEIGHT = 36;
const ACTION_COL_WIDTH_ICON = 60;

const ACTION_COL_WIDTH = 60;
const HEADER_MIN_WIDTH = 100;
const ROW_UNIQUE_KEY = 'MiniDocListDocID';
const FOOTER_HEIGHT = 40;
const INITIAL_LOAD_DELAY = 1000;
const PX_REM = 16;
const FILE_TYPE_FIELDS = new Set(['M--64', 'DocFileType', 'FileType']);

//---------------------- component start -----------------------
const DocViewDocListDataGrid: FC<DocViewDocListDataGridProps> = ({
  panelId,
  miniDocListheight,
  showFooter = true,
  refreshKey,
  codingFormUpdated = false,
}) => {
  const isPopoutOpen = !showFooter;
  const apiRef = useRef<LazyDataGridApi>(null);

  const documentViewLoaded = useRef<boolean>(false);

  const isFirstLoded = useRef<boolean>(false);
  const initialActiveDocument = useRef<MiniDocRowSelection>(null);

  const checkedDocuments = useRef<MiniDocRowSelection[]>([]);

  const activeDocument = useRef<MiniDocRowSelection>(null);
  const completedDocs = useSelector(
    (state: RootState) => state.docView.completedDocs
  );
  const completedDocuments = useRef<MiniDocRowSelection[]>(completedDocs);
  const [columns, setColumns] = useState<ColumnDef<IDocViewDocListRowData>[]>(
    []
  );

  const [rowDataForNavigate, setRowDataForNavigate] =
    useState<IDocViewDocListRowData>();

  const [currentFetchedRowCount, setCurrentFetchedRowCount] = useState(0);

  const docViewConfig = useSelector(
    (state: RootState) => state.docView.docViewConfiguration
  );
  const documentViewConfig = useRef<DocViewResponse | null>(null);

  const docListMasterId = useSelector(
    (state: RootState) => state.docView.documentListMasterId
  );
  const codeSameAsDocs = useSelector(
    (state: RootState) => state.docView.codeSameAsDocs
  );
  const codeSameAsDocuments = useRef<MiniDocRowSelection[]>(codeSameAsDocs);

  const forcedStartIndex = useRef<number | null>(null);

  const pendingScrollToTopDocId = useRef<string | null>(null);

  const codeSameAsTriggerState = useSelector(
    (state: RootState) => state.docView.codeSameAsTriggerState
  );
  const removeCodeSameAsTriggerState = useSelector(
    (state: RootState) => state.docView.removeCodeSameAsTriggerState
  );
  const docViewLoded = useSelector(
    (state: RootState) => state.docView.docViewLoaded
  );
  const activeDoc = useSelector((state: RootState) => state.docView.activeDoc);
  const checkedDocs = useSelector(
    (state: RootState) => state.docView.checkedDocs
  );

  const sourceTypeCode = useSelector(
    (state: RootState) => state.docView.sourceTypeCode
  );

  const isDocumentCompleted = useSelector(
    (state: RootState) => state.docView.isDocumentCompleted
  );

  const getRowDataWithUpdatedState = async (
    rowData: IDocViewDocListRowData[]
  ) => {
    return rowData.map((row) => {
      const currentRow = _.cloneDeep(row);
      if (
        checkedDocuments.current.some(
          (checkedDocument) =>
            checkedDocument.documentId == currentRow.MiniDocListDocID
        )
      ) {
        currentRow.IsChecked = true;
      }
      if (activeDocument.current?.documentId == currentRow.MiniDocListDocID) {
        currentRow.IsSelected = true;
      }

      if (
        codeSameAsDocuments.current.some(
          (codeSameAsDocument) =>
            codeSameAsDocument.documentId == currentRow.MiniDocListDocID
        )
      ) {
        currentRow.IsCodeSame = true;
      }
      if (
        completedDocuments.current.some(
          (completedDocument) =>
            completedDocument.documentId == currentRow.MiniDocListDocID
        )
      ) {
        currentRow.IsCompleted = true;
      }
      return currentRow;
    });
  };

  const fetchRowData = async (
    start: number,
    pageSize: number
  ): Promise<{rows: IDocViewDocListRowData[]; totalRows: number}> => {
    const payload = prepareMiniDocDataRequest(
      start,
      pageSize - 1,
      docListMasterId,
      documentViewConfig.current!,
      sourceTypeCode
    );
    let response: MiniDocListResponse;
    let totalRecords: number = 0;
    let rowsData: IDocViewDocListRowData[] = [];

    try {
      if (documentViewLoaded.current) {
        response = (await getMiniDocListGridData(
          payload
        )) as MiniDocListResponse;
        totalRecords = response.totalRecords;
        const isDocViewerCalledFromMiniDocList =
          forcedStartIndex.current !== null;
        if (!isDocViewerCalledFromMiniDocList && response.totalRecords === 0) {
          setWarningToast({
            showToast: true,
            toastMessage: t('docView.assignment.assignmentWarning'),
            toastType: ToastType.Warning,
          });
        }
        rowsData = JSON.parse(response.serverData) as IDocViewDocListRowData[];
      }
    } catch (error) {
      console.error(error);
    }

    rowsData =
      rowsData?.map((row) => {
        const isCompleted =
          row.IsCompleted === true ||
          Number(row.IsCompleted) === 1 ||
          String(row.IsCompleted) === '1' ||
          String(row.IsCompleted).toLowerCase() === 'true';

        return {
          ...row,
          IsChecked: false,
          IsCodeSame: false,
          IsSelected: false,
          IsCompleted: isCompleted,
        };
      }) ?? [];
    const completedDocsFromBackend = rowsData
      .filter((row) => row.IsCompleted === true)
      .map((row) => ({
        documentId: row.MiniDocListDocID,
        rowNumber: row.RowNumber,
        isCompleted: true,
        docId: row.DocID,
        fileName: row.FileName,
        fileTypeCode: row.FileTypeCode,
        miniMediaLength: row.MiniMediaLength,
        miniAudioTrimmedLength: row.MiniAudioTrimmedLength,
        miniAudioDocDateTimeOnly: row.MiniAudioDocDateTimeOnly,
        docExt: row.MiniDocListDocExt as string,
        documentGUID: row.DocumentGUID,
        ingestionGUID: row.IngestionGUID,
        __InternalExcelProtectedSheets: row.__InternalExcelProtectedSheets,
        __InternalExcelProtectedWorkbook: row.__InternalExcelProtectedWorkbook,
        __InternalHiddenProperties: row.__InternalHiddenProperties,
        __InternalHiddenPropertiesText: row.__InternalHiddenPropertiesText,
        internalVideoPlayerReady: row.__InternalVideoPlayerReady,
      }));

    const existingCompletedDocIds = new Set(
      completedDocsFromBackend.map((d) => d.documentId)
    );
    const existingNotInBackend = completedDocuments.current.filter(
      (doc) => !existingCompletedDocIds.has(doc.documentId)
    );

    completedDocuments.current = [
      ...completedDocsFromBackend,
      ...existingNotInBackend,
    ];

    const updatedRowData = await getRowDataWithUpdatedState(rowsData);

    return {rows: updatedRowData, totalRows: totalRecords};
  };

  useEffect(() => {
    setMiniDocListRowLookup((documentId) => apiRef.current?.getRow(documentId));
    return () => setMiniDocListRowLookup(null);
  }, []);

  useEffect(() => {
    apiRef.current?.reloadData();
  }, [refreshKey]);

  const [currentRows, setCurrentRows] = useState<IDocViewDocListRowData[]>([]);

  const dataSource = React.useMemo(
    () => ({
      getRows: async (startIndex: number, endIndex: number) => {
        const effectiveStart = (() => {
          const f = forcedStartIndex.current;
          if (typeof f === 'number' && Number.isFinite(f) && f > 0) return f;
          return startIndex === 0 ? 1 : startIndex;
        })();

        const safeEndIndex =
          typeof endIndex === 'number' && Number.isFinite(endIndex)
            ? endIndex
            : startIndex + 50;

        const size = Math.max(1, safeEndIndex - effectiveStart);

        const rowDAta = await fetchRowData(effectiveStart, size);
        setTotalDocumentsCount(rowDAta.totalRows);
        setCurrentFetchedRowCount(rowDAta.rows.length);
        setCurrentRows(rowDAta.rows);

        return {
          rows: rowDAta.rows,
          totalRowCount: rowDAta.totalRows,
        };
      },
    }),
    []
  );

  useEffect(() => {
    if (!activeDoc) {
      setNextDocument(null);
    } else if (currentRows?.length) {
      const nextIndex =
        currentRows.findIndex((r) => r.DocID === activeDoc?.docId) + 1;
      if (nextIndex !== currentRows.length) {
        const rowData = currentRows[nextIndex];
        setNextDocument({
          documentId: rowData.MiniDocListDocID,
          rowNumber: rowData.RowNumber,
          isCompleted: rowData.IsCompleted,
          docId: rowData.DocID,
          fileName: rowData.FileName,
          fileTypeCode: rowData.FileTypeCode,
          miniMediaLength: rowData.MiniMediaLength,
          miniAudioTrimmedLength: rowData.MiniAudioTrimmedLength,
          miniAudioDocDateTimeOnly: rowData.MiniAudioDocDateTimeOnly,
          docExt: rowData.MiniDocListDocExt as string,
          documentGUID: rowData.DocumentGUID,
          ingestionGUID: rowData.IngestionGUID,
          __InternalExcelProtectedSheets:
            rowData.__InternalExcelProtectedSheets,
          __InternalExcelProtectedWorkbook:
            rowData.__InternalExcelProtectedWorkbook,
          __InternalHiddenProperties: rowData.__InternalHiddenProperties,
          __InternalHiddenPropertiesText:
            rowData.__InternalHiddenPropertiesText,
          internalVideoPlayerReady: rowData.__InternalVideoPlayerReady,
          ActualParameters: rowData?.ActualParameters ?? '',
          ConceptParameters: rowData?.ConceptParameters ?? '',
          MinConceptScore: rowData?.MinConceptScore,
        });
      }
    }
  }, [currentRows, activeDoc]);

  const checkboxClickHandler = useCallback(
    (rowData: IDocViewDocListRowData) => {
      const hasChecked = checkedDocuments.current.some(
        (checkedDoc) => checkedDoc.documentId == rowData.MiniDocListDocID
      );
      let checkedDocs: MiniDocRowSelection[] = [];
      if (hasChecked) {
        checkedDocs = checkedDocuments.current.filter(
          (checkedDoc) => checkedDoc.documentId != rowData.MiniDocListDocID
        );
        apiRef.current?.updateRow({
          ...rowData,
          IsChecked: false,
        });
      } else {
        checkedDocs = [
          ...checkedDocuments.current,
          {
            documentId: rowData.MiniDocListDocID,
            rowNumber: rowData.RowNumber,
            isCompleted: rowData.IsCompleted,
            docId: rowData.DocID,
            fileName: rowData.FileName,
            fileTypeCode: rowData.FileTypeCode,
            miniMediaLength: rowData.MiniMediaLength,
            miniAudioTrimmedLength: rowData.MiniAudioTrimmedLength,
            miniAudioDocDateTimeOnly: rowData.MiniAudioDocDateTimeOnly,
            docExt: rowData.MiniDocListDocExt as string,
            documentGUID: rowData.DocumentGUID,
            ingestionGUID: rowData.IngestionGUID,
            __InternalExcelProtectedSheets:
              rowData.__InternalExcelProtectedSheets,
            __InternalExcelProtectedWorkbook:
              rowData.__InternalExcelProtectedWorkbook,
            __InternalHiddenProperties: rowData.__InternalHiddenProperties,
            __InternalHiddenPropertiesText:
              rowData.__InternalHiddenPropertiesText,
            internalVideoPlayerReady: rowData.__InternalVideoPlayerReady,
          },
        ];
        apiRef.current?.updateRow({
          ...rowData,
          IsChecked: true,
        });
      }
      setCheckedDocsIdHandler(checkedDocs);
    },
    []
  );
  const rowClickHandler = useCallback((rowData: IDocViewDocListRowData) => {
    if (activeDocument.current?.documentId == rowData.MiniDocListDocID) return;

    const currentRow = apiRef.current?.getRow(
      rowData.MiniDocListDocID
    ) as IDocViewDocListRowData;
    const previousRow = apiRef.current?.getRow(
      activeDocument?.current?.documentId ?? ''
    ) as IDocViewDocListRowData;

    if (previousRow) {
      apiRef.current?.updateRow({
        ...previousRow,
        IsSelected: false,
      });
    }
    if (currentRow) {
      apiRef.current?.updateRow({
        ...currentRow,
        IsSelected: true,
      });
    }

    setActiveDocHandler(rowData);
  }, []);
  const getRowClass = useCallback((rowData: IDocViewDocListRowData) => {
    if (rowData.IsSelected) return 'row-selected';
    if (rowData.IsCodeSame) return 'row-code-same-as';
    return '';
  }, []);
  const docViewListGridColumn = useCallback(
    (
      miniDocColumns: MiniDocListColumnInfo[]
    ): ColumnDef<IDocViewDocListRowData>[] => {
      const actionColumnWidth =
        completedDocuments.current.length || codeSameAsDocs?.length
          ? ACTION_COL_WIDTH_ICON
          : ACTION_COL_WIDTH;
      const actionColumn: ColumnDef<IDocViewDocListRowData> = {
        field: '__action__',
        headerName: '',
        cellClassName: 'action-cell',
        minWidth: actionColumnWidth,
        maxWidth: actionColumnWidth,
        renderCell: ({row}) => {
          return (
            <ActionCell rowData={row} onCheckboxClick={checkboxClickHandler} />
          );
        },
      } as ColumnDef<IDocViewDocListRowData>;
      const visibleColumns: MiniDocListColumnInfo[] = miniDocColumns.filter(
        (col) => col.visible && col.title != ''
      );
      const selectedColumns: ColumnDef<IDocViewDocListRowData>[] = [];
      for (const [index, col] of visibleColumns.entries()) {
        selectedColumns.push({
          field: col.data,
          headerClassName:
            index == visibleColumns.length - 1 ? 'last-column ' : '',
          flex: 1,
          minWidth: HEADER_MIN_WIDTH,
          width: HEADER_MIN_WIDTH,
          renderHeader: () => (
            <TruncatedTooltip
              content={col.title}
              dataTestId={`cell-wrapper-tooltip-content-selected-${col.data}-${col.title}`}
              className="custom-header-content"
              typotestId={`doc-view-doc-list-data-grid-title-${col.data}`}
            />
          ),
          renderCell: (param) => {
            const cellValue =
              FILE_TYPE_FIELDS.has(col.data) &&
              (param.value === null ||
                param.value === undefined ||
                String(param.value).trim() === '')
                ? param.row?.DocFileType
                : String(param.value ?? '');

            return (
              <TruncatedTooltip
                content={cellValue}
                dataTestId={`cell-wrapper-tooltip-content-selected-${col.data}-${col.title}`}
                className="custom-cell-content typo-text body4_size"
                typotestId={`doc-view-doc-list-data-grid-typography-text-${col.data}-${param.rowIndex}`}
              />
            );
          },
        });
      }
      return [actionColumn, ...selectedColumns];
    },
    []
  );

  const clearAllCheckBoxHandler = () => {
    for (const checkedDoc of checkedDocuments.current) {
      const rowData = apiRef.current?.getRow(
        checkedDoc.documentId
      ) as IDocViewDocListRowData;
      if (rowData) {
        apiRef.current?.updateRow({
          ...rowData,
          IsChecked: false,
        });
      }
    }
    checkedDocuments.current = [];
    clearCheckedDocuments();
  };
  const setCodeSameAsDocsIdHandler = async (
    codeSameAsDocs: MiniDocRowSelection[]
  ) => {
    let docsStatus;
    try {
      docsStatus = (await getDocIsCompleted({
        assignmentSource: '',
        sourceID: Number(docListMasterId),
        sourceTypeCode: 'DOCUMENT',
        selectedDocs: codeSameAsDocs.map((doc) => doc.documentId),
      })) as DocCompletedCheckResponse;
    } catch (error) {
      console.error(error);
    }

    if (!codeSameAsDocs.length) {
      setDocumentCodeSameAsToastState({
        showToast: true,
        toastMessage: t('docView.codeSameAs.codeSameAsMinDocSelection'),
        toastType: ToastType.Warning,
      });
      triggerCodeSameAsRendering({isTriggered: false});
      return;
    }
    if (
      codeSameAsDocs.some((doc) => doc.isCompleted) ||
      docsStatus?.docsCompleted != ''
    ) {
      setDocumentCodeSameAsToastState({
        showToast: true,
        toastMessage: t('docView.codeSameAs.codeSameAsWaring'),
        toastType: ToastType.Warning,
      });
      triggerCodeSameAsRendering({isTriggered: false});
      return;
    }

    setCodeSameAsDocuments(codeSameAsDocs);
    clearAllCheckBoxHandler();
    triggerCodeSameAsRendering({isTriggered: false});
    setDocumentCodeSameAsToastState({
      showToast: true,
      toastMessage: t('docView.codeSameAs.codeSameAsSuccess'),
      toastType: ToastType.Success,
    });
    apiRef.current?.reloadData();
  };

  const setRemoveCodeSameAsDocsIdHandler = (
    codeSameAsDocs: MiniDocRowSelection[]
  ) => {
    if (!codeSameAsDocs.length) {
      setDocumentCodeSameAsToastState({
        showToast: true,
        toastMessage: t('docView.codeSameAs.codeSameAsMinDocSelection'),
        toastType: ToastType.Warning,
      });
      triggerRemoveCodeSameAsRendering({isTriggered: false});
      return;
    }

    removeCodeSameAsDocuments(codeSameAsDocs);
    clearAllCheckBoxHandler();
    triggerRemoveCodeSameAsRendering({isTriggered: false});
    setDocumentCodeSameAsToastState({
      showToast: true,
      toastMessage: t('docView.codeSameAs.removeCodeSameAsSuccess'),
      toastType: ToastType.Success,
    });
    apiRef.current?.reloadData();
  };

  const setCheckedDocsIdHandler = (checkedDoc: MiniDocRowSelection[]) => {
    checkedDocuments.current = checkedDoc;
    setCheckedDocuments(checkedDocuments.current);
  };

  const rowsRenderedHandler = (rows: IDocViewDocListRowData[]) => {
    scrollSelectedRowToTopIfLoaded();
    if (rows.length && !isFirstLoded.current) {
      isFirstLoded.current = true;
      if (initialActiveDocument.current) {
        applyActiveDocument({...initialActiveDocument.current});
        return;
      }
      rowClickHandler(rows[0]);
    }
  };

  const setActiveDocHandler = (rowData: IDocViewDocListRowData) => {
    if (rowData) {
      activeDocument.current = {
        documentId: rowData.MiniDocListDocID,
        rowNumber: rowData.RowNumber,
        isCompleted: rowData.IsCompleted,
        docId: rowData.DocID,
        fileName: rowData.FileName,
        fileTypeCode: rowData.FileTypeCode,
        miniMediaLength: rowData.MiniMediaLength,
        miniAudioTrimmedLength: rowData.MiniAudioTrimmedLength,
        miniAudioDocDateTimeOnly: rowData.MiniAudioDocDateTimeOnly,
        docExt: rowData.MiniDocListDocExt as string,
        documentGUID: rowData.DocumentGUID,
        ingestionGUID: rowData.IngestionGUID,
        __InternalExcelProtectedSheets: rowData.__InternalExcelProtectedSheets,
        __InternalExcelProtectedWorkbook:
          rowData.__InternalExcelProtectedWorkbook,
        __InternalHiddenProperties: rowData.__InternalHiddenProperties,
        __InternalHiddenPropertiesText: rowData.__InternalHiddenPropertiesText,
        internalVideoPlayerReady: rowData.__InternalVideoPlayerReady,
        ActualParameters: rowData?.ActualParameters ?? '',
        ConceptParameters: rowData?.ConceptParameters ?? '',
        MinConceptScore: rowData?.MinConceptScore,
      };
      setActiveDocument(activeDocument.current);
      storeDocIdInHistory(activeDocument.current);
    }
  };

  const applyActiveDocument = (doc: MiniDocRowSelection) => {
    const currentActiveRow = apiRef.current?.getRow(
      activeDocument?.current?.documentId ?? ''
    ) as IDocViewDocListRowData;
    if (
      currentActiveRow &&
      currentActiveRow.MiniDocListDocID !== doc.documentId
    ) {
      apiRef.current?.updateRow({
        ...currentActiveRow,
        IsSelected: false,
      });
    }
    activeDocument.current = doc;
  };

  const getRowNumber = useCallback(
    async (doc: MiniDocRowSelection) => {
      const fieldOrder =
        docViewConfig.sortconfiguration?.selectFieldDataType
          ?.map((f) => f.fieldId)
          .join(',') ?? '';
      const selectedFields =
        docViewConfig.sortconfiguration?.selectFields ?? '';
      const showCompletedDocs =
        docViewConfig.sortconfiguration?.configuredShowCompletedDoc ?? false;

      const payload = {
        docViewViewingTimeStamp: getCurrentDateTime(),
        docId: Number(doc.documentId),
        selectFields: selectedFields,
        configuredShowCompletedDoc: showCompletedDocs,
        orderFieldIDList: fieldOrder,
        documentListMasterID: Number(docListMasterId),
        sourceTypeCode: sourceTypeCode ?? 'DOCUMENT',
      };

      return await getRowNumberForDocument(payload);
    },

    [docViewConfig, docListMasterId, sourceTypeCode]
  );

  const updateActiveDocRowNumber = useCallback(
    async (documentId: string) => {
      if (!activeDocument.current) return null;

      if (activeDocument.current.documentId !== documentId) return null;

      try {
        // call API
        const res = await getRowNumber(activeDocument.current);

        // Normalize response: adjust according to your actual API response shape
        const apiRowNumber =
          typeof res === 'number'
            ? res
            : Number(res?.rowNumber ?? res?.RowNumber ?? 0);

        if (!apiRowNumber || !Number.isFinite(apiRowNumber)) return null;

        // update only if changed
        if (activeDocument.current.rowNumber !== apiRowNumber) {
          const updated = {...activeDocument.current, rowNumber: apiRowNumber};
          activeDocument.current = updated;
          setActiveDocument(updated);
          storeDocIdInHistory(updated);
        }

        return apiRowNumber;
      } catch (e) {
        console.error(e);
        return null;
      }
    },

    [getRowNumber]
  );

  const scrollSelectedRowToTopIfLoaded = useCallback(async () => {
    const id = pendingScrollToTopDocId.current;

    if (!id) return;

    // Prefer the rowNumber we already know (from activeDocument.current)
    const targetRowNumber = Number(activeDocument.current?.rowNumber ?? 0);

    // If we don't have a rowNumber yet, try API once
    let rowNumberToUse = targetRowNumber;

    if (!rowNumberToUse) {
      const apiRowNumber = await updateActiveDocRowNumber(id);
      rowNumberToUse = Number(apiRowNumber ?? 0);
    }

    if (!rowNumberToUse) return;

    const indexToScroll = Math.max(0, rowNumberToUse - 1);

    requestAnimationFrame(() => {
      apiRef.current?.scrollToIndex(indexToScroll);

      // after scroll, mark selected if row is now present
      const row = apiRef.current?.getRow(id) as
        | IDocViewDocListRowData
        | undefined;

      if (row) apiRef.current?.updateRow({...row, IsSelected: true});
    });

    pendingScrollToTopDocId.current = null;
  }, [updateActiveDocRowNumber]);

  useEffect(() => {
    setColumns(docViewListGridColumn(docViewConfig.docHeaders ?? []));
    documentViewConfig.current = docViewConfig;
  }, [docViewConfig]);

  useEffect(() => {
    if (codeSameAsTriggerState.isTriggered) {
      setCodeSameAsDocsIdHandler(checkedDocuments.current);
    }
  }, [codeSameAsTriggerState]);

  useEffect(() => {
    if (removeCodeSameAsTriggerState.isTriggered) {
      setRemoveCodeSameAsDocsIdHandler(checkedDocuments.current);
    }
  }, [removeCodeSameAsTriggerState]);

  useEffect(() => {
    const prevDocs = codeSameAsDocuments.current ?? [];
    const newDocs = codeSameAsDocs ?? [];

    if (documentViewLoaded.current && isFirstLoded.current) {
      const removedDocs = prevDocs.filter(
        (prev) => !newDocs.some((doc) => doc.documentId === prev.documentId)
      );

      for (const doc of removedDocs) {
        const row = apiRef.current?.getRow(
          doc.documentId
        ) as IDocViewDocListRowData;
        if (row) {
          apiRef.current?.updateRow({
            ...row,
            IsCodeSame: false,
          });
        }
      }

      const addedDocs = newDocs.filter(
        (newDoc) =>
          !prevDocs.some((prev) => prev.documentId === newDoc.documentId)
      );

      for (const doc of addedDocs) {
        const row = apiRef.current?.getRow(
          doc.documentId
        ) as IDocViewDocListRowData;
        if (row) {
          apiRef.current?.updateRow({
            ...row,
            IsCodeSame: true,
          });
        }
      }
    }

    // Update ref AFTER processing
    codeSameAsDocuments.current = codeSameAsDocs;
  }, [codeSameAsDocs]);

  useEffect(() => {
    completedDocuments.current = completedDocs;
  }, [completedDocs]);

  useEffect(() => {
    if (!isFirstLoded.current) {
      initialActiveDocument.current = activeDoc;
      activeDocument.current = activeDoc;
      return;
    }
    if (!activeDoc) return;
    if (activeDoc.documentId == activeDocument?.current?.documentId) return;

    const prev = activeDocument.current;

    const sameDoc = prev?.documentId === activeDoc.documentId;

    const rowChanged = prev?.rowNumber !== activeDoc.rowNumber;

    // if same doc but row number changed -> we still must scroll

    if (sameDoc && rowChanged) {
      pendingScrollToTopDocId.current = activeDoc.documentId;
      // update local ref to new rowNumber
      activeDocument.current = {...prev, rowNumber: activeDoc.rowNumber};
      apiRef.current?.reloadData();
      return;
    }

    // if doc id changed

    if (!sameDoc) {
      pendingScrollToTopDocId.current = activeDoc.documentId;
      applyActiveDocument(activeDoc);
      apiRef.current?.reloadData();
    }
  }, [activeDoc]);

  useEffect(() => {
    if (docViewLoded) {
      documentViewLoaded.current = true;

      setTimeout(() => {
        apiRef.current?.reloadData();
      }, INITIAL_LOAD_DELAY);
    }
  }, [docViewLoded]);

  useEffect(() => {
    checkedDocuments.current = checkedDocs;
  }, [checkedDocs]);

  useEffect(() => {
    if (activeDoc) {
      if (isDocumentCompleted) {
        const exists = completedDocuments.current.some(
          (doc) => doc.documentId === activeDoc.documentId
        );
        if (!exists) {
          completedDocuments.current = [
            ...completedDocuments.current,
            activeDoc,
          ];
          apiRef.current?.reloadData();
        }
      } else {
        const exists = completedDocuments.current.some(
          (doc) => doc.documentId === activeDoc.documentId
        );
        if (exists) {
          completedDocuments.current = completedDocuments.current.filter(
            (doc) => doc.documentId !== activeDoc.documentId
          );
          apiRef.current?.reloadData();
        }
      }
    }
  }, [isDocumentCompleted, activeDoc?.documentId]);

  const getAdjustedContainerHeight = () => {
    const visibleGridHeight =
      currentFetchedRowCount * ROW_HEIGHT + ROW_HEIGHT + FOOTER_HEIGHT;
    return visibleGridHeight;
  };
  return (
    <Box
      className={`doc-view-doc-list-dat-grid ${isPopoutOpen ? 'with-popout' : ''}`}
      data-testid="mini-doc-list-data-grid-container"
      height={
        getAdjustedContainerHeight() > miniDocListheight || isPopoutOpen
          ? `${(miniDocListheight - ROW_HEIGHT) / PX_REM}rem`
          : '100%'
      }
      data-tab-group={panelId}
      data-tab-id="docview-doclist-data-grid"
    >
      <ArrModal
        showModal={!!rowDataForNavigate}
        title={t('docView.headers.codingForm.loseChanges')}
        showClose
        onClose={() => {
          setRowDataForNavigate(undefined);
        }}
        dataTestId="docview-doclist-datagrid-modal"
        buttons={[
          {
            id: '1',
            label: t('docView.headers.codingForm.loseChangesCancel'),
            onClick: () => {
              setRowDataForNavigate(undefined);
            },
            variant: 'secondary',
            dataTestId: 'ddd-cancel-btn',
          },
          {
            id: '2',
            label: t('docView.headers.codingForm.loseChangesOK'),

            onClick: () => {
              if (rowDataForNavigate) {
                rowClickHandler(rowDataForNavigate);
              }
              setRowDataForNavigate(undefined);
            },
            variant: 'primary',
            dataTestId: 'ddd-ok-btn',
          },
        ]}
        description={t('docView.headers.codingForm.loseChangesOnDocSwitch')}
      ></ArrModal>
      <LazyDataGrid
        panelId={panelId}
        dataTestId="mini-doc-list-data-grid"
        rowHeight={ROW_HEIGHT}
        headerHeight={ROW_HEIGHT}
        ref={apiRef}
        columns={columns}
        uniqueDataKey={ROW_UNIQUE_KEY}
        dataSource={dataSource}
        onRowClick={(row) => {
          if (codingFormUpdated) {
            setRowDataForNavigate(row);
          } else {
            rowClickHandler(row);
          }
        }}
        getRowClass={(row) => getRowClass(row)}
        onRowsRendered={rowsRenderedHandler}
        showFooter={showFooter}
        footerHeight={FOOTER_HEIGHT}
        footer={showFooter && <FooterPanel panelId={panelId} />}
      />
    </Box>
  );
};

export const prepareMiniDocDataRequest = (
  pageNumber: number,
  pageSize: number,
  docListMasterId: string,
  docViewConfig: DocViewResponse,
  sourceTypeCode?: string
): MiniDocListRequest => {
  const redirection = docViewConfig.sortconfiguration?.redirectionType ?? 1;
  const selectedFields = docViewConfig.sortconfiguration?.selectFields ?? '';
  const fieldOrder =
    docViewConfig.sortconfiguration?.selectFieldDataType
      ?.map((f) => f.fieldId)
      .join(',') ?? '';
  const manualOrders =
    redirection == SortType.manual
      ? docViewConfig.sortconfiguration?.order
      : fieldOrder;
  const showCompletedDocs =
    docViewConfig.sortconfiguration?.configuredShowCompletedDoc ?? false;
  return {
    pageSize: pageSize,
    pageNumber: pageNumber,
    selectFields: selectedFields,
    configuredShowCompletedDoc: showCompletedDocs,
    orderFieldIDList: fieldOrder,
    redirectionType: redirection,
    manualSelectFieldIDs: manualOrders,
    manualSelectFieldOrder: selectedFields,
    manualOrder: manualOrders,
    optimizedSelectFieldIDs:
      redirection == SortType.optimized ? selectedFields : '',
    optimizedSelectFieldOrder:
      redirection == SortType.optimized ? selectedFields : '',
    documentListMasterID: Number(docListMasterId),
    sourceTypeCode: sourceTypeCode ?? 'DOCUMENT',
    docsForCodeSame: [],
    docViewViewingTimeStamp: getCurrentDateTime(),
  };
};

const getCurrentDateTime = (): string => {
  const now = new Date();

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0'); // months are 0-indexed
  const day = String(now.getDate()).padStart(2, '0');

  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');

  return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
};

export default React.memo(DocViewDocListDataGrid);
